import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BanknoteIcon, CalendarPlusIcon, CheckIcon, ChevronRightIcon, ClockIcon, FolderOpenIcon, MapPinIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { missingFields } from '../../utils/personal';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Donut } from '../../components/ui/Donut';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Waves } from '../../components/ui/Logo';
import { AbsenceModal } from '../../components/member/AbsenceModal';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, formatShort, priorityClass, priorityLabel, relativeDays } from '../../utils/labels';
const donutColor: Record<string, string> = {
  zelena: '#007c69',
  oranzna: '#d97706',
  rdeca: '#e11d48',
  premalo: '#cbd5e1'
};
export function MemberHome() {
  const {
    currentMember,
    upcoming,
    visibleEvents,
    getRsvp,
    setRsvp,
    settings,
    announcements,
    fees,
    today,
    isExpected,
    pushToast,
    rooms,
    transport
  } = useApp();
  const navigate = useNavigate();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const me = currentMember!;
  const jeGost = me.type === 'gostujoci';
  const mojiDogodki = useMemo(() => visibleEvents(me.id).filter(e => upcoming.some(u => u.id === e.id)), [visibleEvents, upcoming, me.id]);
  const naslednji = mojiDogodki[0];
  const rsvp = naslednji ? getRsvp(naslednji.id) : undefined;
  const potovalnaOpozorila = useMemo(() => {
    return mojiDogodki.filter(e => e.potovanje?.aktivno && isExpected(e, me.id)).flatMap(e => {
      const t = e.potovanje!;
      const opozorila: string[] = [];
      if (missingFields(me, t.obveznaPolja).length > 0) {
        opozorila.push('Za gostovanje morate dopolniti osebne podatke.');
      }
      if (t.nastanitev?.aktivno && !rooms[e.id]?.[me.id]) {
        opozorila.push('Soba še ni določena.');
      }
      if (t.prevoz?.aktivno && !transport[e.id]?.[me.id]) {
        opozorila.push('Za gostovanje še nimate urejenega prevoza.');
      }
      return opozorila.map(besedilo => ({
        event: e,
        besedilo
      }));
    });
  }, [mojiDogodki, isExpected, me, rooms, transport]);
  const povzetek = attendanceSummary(me, settings);
  const zadnjeObvestilo = announcements.find(a => a.prejemniki.includes(me.id));
  const neprebrana = announcements.filter(a => a.prejemniki.includes(me.id) && !a.prebrali.includes(me.id)).length;
  const clanarina = fees.find(f => f.memberId === me.id && f.leto === settings.letoClanarine);
  return <div className="space-y-5" data-id="element-1560" data-mp="13s29uf4bz7vy">
      <div data-id="element-1561" data-mp="13r0mbb64osxc">
        <p className="text-sm text-ink-muted" data-id="element-1562" data-mp="13p6bipokmh6z">{formatDate(today, {
          weekday: true,
          long: true
        })}</p>
        <h1 className="mt-0.5 text-2xl font-semibold text-ink" data-id="element-1563" data-mp="3fcjb6zk3o8sc">Pozdravljeni, {me.ime}.</h1>
      </div>

      {naslednji && <Card className="relative overflow-hidden" data-id="element-1564" data-mp="36e7ov9q9nx5p">
          <Waves className="pointer-events-none absolute -right-10 top-0 h-24 w-72" opacity={0.1} data-id="element-1565" data-mp="36finkv4uuepy" />
          <div className="relative p-5" data-id="element-1566" data-mp="3ag2psd4ac8iy">
            <div className="flex items-center justify-between gap-3" data-id="element-1567" data-mp="1zzfdzlsg5qx">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1568" data-mp="1xmxtnjxzxee">
                Naslednji dogodek
              </p>
              <Badge className="bg-green-50 text-green-700 border-green-200" data-id="element-1569" data-mp="3p58kpow3bcl1">
                {relativeDays(naslednji.datum, today)}
              </Badge>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1570" data-mp="2dpa5t4e8dwbw">
              <Badge className={eventTypeClass[naslednji.type]} data-id="element-1571" data-mp="2dnz4db89d0ny">{eventTypeShort[naslednji.type]}</Badge>
              {naslednji.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-1572" data-mp="2xyjrdyxd3ln2">{naslednji.opomba}</Badge>}
            </div>

            <h2 className="mt-2 text-xl font-semibold text-ink" data-id="element-1573" data-mp="2t6i7prxv6sn0">{eventTitle(naslednji)}</h2>
            <div className="mt-2 space-y-1 text-sm text-ink-muted" data-id="element-1574" data-mp="2o98hwb6kceyn">
              <p className="flex items-center gap-1.5" data-id="element-1575" data-mp="2k6vcxozn4l2x">
                <ClockIcon className="h-4 w-4" data-id="element-1576" data-mp="2k8ga7mdcai4h" />
                {formatDate(naslednji.datum, {
              weekday: true
            })} · {naslednji.zacetek}
                {naslednji.konec ? `–${naslednji.konec}` : ''}
              </p>
              <p className="flex items-center gap-1.5" data-id="element-1577" data-mp="3bvyl3funefku">
                <MapPinIcon className="h-4 w-4" data-id="element-1578" data-mp="37uv5mugiilk5" />
                {naslednji.lokacija}
              </p>
            </div>

            {isExpected(naslednji, me.id) ? <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-1579" data-mp="1whkbs6edooo9">
                <Button variant={rsvp?.status === 'pridem' ? 'primary' : 'secondary'} onClick={() => {
            setRsvp(naslednji.id, {
              status: 'pridem'
            });
            pushToast('Zabeleženo — se vidimo na vaji.');
          }} data-id="element-1580" data-mp="1wey5sb74yh85">
                  <CheckIcon className="h-4 w-4" data-id="element-1581" data-mp="bwm7khd5rf9b" />
                  Pridem
                </Button>
                <Button variant={rsvp?.status === 'ne_morem' ? 'danger' : 'secondary'} onClick={() => setAbsenceOpen(true)} data-id="element-1582" data-mp="fyqxp64edtr7">
                  <XIcon className="h-4 w-4" data-id="element-1583" data-mp="pq56y5m5i7xj" />
                  Ne morem priti
                </Button>
              </div> : <p className="mt-4 rounded-lg bg-canvas px-3 py-2 text-[13px] text-ink-muted" data-id="element-1584" data-mp="zouf36o8we0l">
                Za ta dogodek vaša prisotnost ni pričakovana.
              </p>}

            {rsvp?.status === 'ne_morem' && <p className="mt-2 text-[13px] text-ink-muted" data-id="element-1585" data-mp="31h93molaogpz">
                Sporočili ste odsotnost{rsvp.razlog ? ` — ${rsvp.razlog.toLowerCase()}` : ''}.
              </p>}

            <div className="mt-4 flex items-center justify-between border-t border-line pt-3" data-id="element-1586" data-mp="co3x99d99qt1">
              <Link to={`/clan/dogodki/${naslednji.id}`} className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-1587" data-mp="7or7afr383m8">
                Podrobnosti dogodka
              </Link>
              <Link to="/clan/odsotnost" className="flex items-center gap-1.5 text-[13px] font-medium text-ink-muted hover:text-ink" data-id="element-1588" data-mp="hkknc940mf8r">
                <CalendarPlusIcon className="h-4 w-4" data-id="element-1589" data-mp="qm0um7zja18r" />
                Daljša odsotnost
              </Link>
            </div>
          </div>
        </Card>}

      {potovalnaOpozorila.length > 0 && <Card className="border-amber-200 bg-amber-50/60 p-4" data-id="element-1590" data-mp="10d59z8t4fo44">
          <div className="flex items-start gap-3" data-id="element-1591" data-mp="1ebv88rroftu2">
            <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-1592" data-mp="1eco8d9w4rl38" />
            <div className="min-w-0 flex-1" data-id="element-1593" data-mp="19ewe4980jmvv">
              <p className="text-sm font-medium text-ink" data-id="element-1594" data-mp="1szd2sxbiz82r">
                {eventTitle(potovalnaOpozorila[0].event)} — potrebna vaša dopolnitev
              </p>
              <ul className="mt-1 space-y-0.5 text-[13px] text-ink-muted" data-id="element-1595" data-mp="1xxex6eeeittj">
                {potovalnaOpozorila.slice(0, 3).map(o => <li key={o.besedilo} data-id="element-1596" data-mp="1nzqp6mrsbptf">{o.besedilo}</li>)}
              </ul>
              <Link to={`/clan/dogodki/${potovalnaOpozorila[0].event.id}`} className="mt-2 inline-block text-[13px] font-medium text-green-700 hover:underline" data-id="element-1597" data-mp="1a8lyia6ea7v8">
                Odpri gostovanje
              </Link>
            </div>
          </div>
        </Card>}

      <div className="grid gap-4 md:grid-cols-2" data-id="element-1598" data-mp="158zjbv70ahjs">
        <Card className="p-5" data-id="element-1599" data-mp="159shi7rwfyqb">
          <div className="flex items-start justify-between gap-3" data-id="element-1600" data-mp="r9rlb5mkxfh6">
            <div data-id="element-1601" data-mp="rajc8358ep14">
              <h3 className="text-base font-semibold text-ink" data-id="element-1602" data-mp="rc3zd7p7v5oy">Moja prisotnost</h3>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1603" data-mp="waxc2xwoiso2">Sezona 2026/27</p>
            </div>
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} showPercent={false} data-id="element-1604" data-mp="1borjt0hsym2t" />}
          </div>

          <div className="mt-4 flex items-center gap-5" data-id="element-1605" data-mp="11so8p54z3d3q">
            <Donut odstotek={povzetek.status === 'premalo' ? null : povzetek.odstotek} color={donutColor[povzetek.status]} label="prisotnost" data-id="element-1606" data-mp="11r3fpjyb427z" />
            <div className="text-sm" data-id="element-1607" data-mp="stko1wq5qpn4">
              <p className="text-ink" data-id="element-1608" data-mp="o19sf62g9q0d">
                <span className="text-lg font-semibold" data-id="element-1609" data-mp="o08can6kudbs">{povzetek.prisoten}</span> od{' '}
                {povzetek.pricakovan} dogodkov
              </p>
              {jeGost ? <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1610" data-mp="e3ls80m2rqat">
                  Kot gostujoča članica niste vključeni v semafor prisotnosti.
                </p> : povzetek.status === 'premalo' ? <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1611" data-mp="52xgjck5m213">
                  Semafor se vklopi po {settings.minDogodkov} štetih dogodkih.
                </p> : <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1612" data-mp="2lnajxqp28kae">
                  Zelena meja je {settings.pragZelena} %.
                </p>}
              <Link to="/clan/prisotnost" className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-green-600 hover:underline" data-id="element-1613" data-mp="2ups9of3xbv25">
                Poglej podrobno
                <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1614" data-mp="221e7ro9pselb" />
              </Link>
            </div>
          </div>
        </Card>

        <div className="space-y-4" data-id="element-1615" data-mp="26xuz0rc7n92m">
          {zadnjeObvestilo && <Card className="p-5" data-id="element-1616" data-mp="2bs8vkx2lzm8v">
              <div className="flex items-center justify-between gap-3" data-id="element-1617" data-mp="2bsipuwuosfkd">
                <h3 className="text-base font-semibold text-ink" data-id="element-1618" data-mp="1eb0ddr3nq3nk">Zadnje obvestilo</h3>
                {neprebrana > 0 && <Badge className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-1619" data-mp="1j80le7k2hghb">
                    {neprebrana} neprebrano
                  </Badge>}
              </div>
              <Link to={`/clan/obvestila/${zadnjeObvestilo.id}`} className="mt-3 block group" data-id="element-1620" data-mp="3ezfadce5vxcc">
                <Badge className={priorityClass[zadnjeObvestilo.prioriteta]} data-id="element-1621" data-mp="3juchuevslyn4">
                  {priorityLabel[zadnjeObvestilo.prioriteta]}
                </Badge>
                <p className="mt-2 text-sm font-semibold text-ink group-hover:text-green-700" data-id="element-1622" data-mp="2r92wlkw8j6du">
                  {zadnjeObvestilo.naslov}
                </p>
                <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-ink-muted" data-id="element-1623" data-mp="3167lvtldnhyv">
                  {zadnjeObvestilo.besedilo}
                </p>
              </Link>
              <Link to="/clan/obvestila" className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-green-600 hover:underline" data-id="element-1624" data-mp="35yqvkfgy1sye">
                Vsa obvestila
                <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1625" data-mp="25wkii4aq7acl" />
              </Link>
            </Card>}

          <Card className="divide-y divide-line" data-id="element-1626" data-mp="20x7wflroi7b2">
            <button onClick={() => navigate('/clan/gradiva')} className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1627" data-mp="20yixhdtqp0yh">
              <FolderOpenIcon className="h-5 w-5 text-green-600" data-id="element-1628" data-mp="1r1xvz2gxi7ze" />
              <span className="flex-1" data-id="element-1629" data-mp="1mzj987a52o2u">
                <span className="block text-sm font-medium text-ink" data-id="element-1630" data-mp="1i79m0z0es249">Gradiva zbora</span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1631" data-mp="1i5f4zvs9cu5g">Note, posnetki, Google Drive</span>
              </span>
              <ChevronRightIcon className="h-4 w-4 text-ink-muted" data-id="element-1632" data-mp="189lsgcejh0no" />
            </button>
            <div className="flex items-center gap-3 px-5 py-4" data-id="element-1633" data-mp="139r34daewj4q">
              <BanknoteIcon className="h-5 w-5 text-purple-500" data-id="element-1634" data-mp="1397mczwi00sa" />
              <span className="flex-1" data-id="element-1635" data-mp="z9gn07swocc5">
                <span className="block text-sm font-medium text-ink" data-id="element-1636" data-mp="zai7f2vtab6j">
                  Članarina {settings.letoClanarine}
                </span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1637" data-mp="tmn2klvbyeb2">
                  {clanarina?.znesek ?? settings.znesekClanarine} € · plačano{' '}
                  {clanarina?.datumPlacila ? formatDate(clanarina.datumPlacila) : '—'}
                </span>
              </span>
              <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1638" data-mp="18bjbsm94hxd9">
                Poravnano
              </Badge>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-5" data-id="element-1639" data-mp="ylzpt7q45ne6">
        <div className="flex items-center justify-between gap-3" data-id="element-1640" data-mp="ykom17wo4fn2">
          <h3 className="text-base font-semibold text-ink" data-id="element-1641" data-mp="13kawxap0xjsp">Prihajajoči dogodki</h3>
          <Link to="/clan/dogodki" className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-1642" data-mp="1ra5axvl2xeej">
            Vsi dogodki
          </Link>
        </div>
        <ul className="mt-3 divide-y divide-line" data-id="element-1643" data-mp="1w2ewkzf5vsb0">
          {mojiDogodki.slice(1, 5).map(e => <li key={e.id} data-id="element-1644" data-mp="25e4c3lual8lp">
              <Link to={`/clan/dogodki/${e.id}`} className="flex items-center gap-3 py-3 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1645" data-mp="25db1g9s7pyya">
                <span className="w-16 shrink-0 text-[13px] font-medium text-ink-muted" data-id="element-1646" data-mp="2f7bcste380fg">
                  {formatShort(e.datum)}
                </span>
                <span className="min-w-0 flex-1" data-id="element-1647" data-mp="1mhuoa6tl93oi">
                  <span className="block truncate text-sm font-medium text-ink" data-id="element-1648" data-mp="1mimjhgj9wze9">{eventTitle(e)}</span>
                  <span className="block truncate text-[13px] text-ink-muted" data-id="element-1649" data-mp="1ihkfynibq16h">
                    {e.zacetek} · {e.lokacija}
                  </span>
                </span>
                <Badge className={eventTypeClass[e.type]} data-id="element-1650" data-mp="1rel20nhx7n6r">{eventTypeShort[e.type]}</Badge>
              </Link>
            </li>)}
        </ul>
      </Card>

      <AbsenceModal event={naslednji ?? null} open={absenceOpen} onClose={() => setAbsenceOpen(false)} data-id="element-1651" data-mp="32vukb6pbc96h" />
    </div>;
}